import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { errMessage } from '../../components/UI/ink';
import { integrationsApi } from '../../api/integrations';
import AutoInvestigationCard from '../../components/Settings/AutoInvestigationCard';
import TriageQualityCard from '../../components/Settings/TriageQualityCard';
import AiUsageCard from '../../components/Settings/AiUsageCard';
import { Notice, SectionHead, Switch, panel } from '../../components/Settings/ui';

const NAME = { ollama: 'Ollama', claude: 'Claude', openrouter: 'OpenRouter' };

// What the AI does on its own, how good its verdicts are, and what may
// leave the network. It never runs a response action itself.
export default function AiSettings({ onGo }) {
  const { permissions = [] } = useAuth();
  const canEdit = permissions.includes('system:update');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => integrationsApi.aiPolicy().then(setData).catch(err => setError(errMessage(err, 'Could not load the AI policy.'))), []);
  useEffect(() => { load(); }, [load]);

  const set = async (key, value) => {
    setBusy(key); setError(null);
    try { setData(await integrationsApi.setAiPolicy({ [key]: value })); } catch (err) { setError(errMessage(err, 'Could not save.')); } finally { setBusy(null); }
  };

  const order = data?.model_order || [];
  const uses = order.length
    ? order.map((m, i) => `${i === 0 ? '' : i === 1 ? 'then ' : 'then '}${NAME[m.provider]} (${m.model})`).join(', ')
    : 'No model is set up, so triage and investigations are skipped.';
  const toggles = [
    ['allow_external', 'Allow external models', 'When off, only Ollama is used. If it isn’t set up, triage and investigations are skipped instead of falling back to Claude or OpenRouter.'],
    ['send_identifiers', 'Send hostnames and IP addresses to external models', 'When off, they become placeholders like [HOST-1] and [IP-1] before leaving, and are put back in the answer. Ollama always sees them.'],
  ];

  return (
    <div className="flex flex-col gap-[18px]">
      <SectionHead title="AI & automation" sub="What the AI does on its own, and how often analysts agree with it. It never runs a response action itself." />
      {error && <Notice>{error}</Notice>}

      <div className="flex flex-wrap items-center gap-2 px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ ...panel, color: 'var(--ink-muted)' }}>
        <span className="font-semibold" style={{ color: 'var(--ink-text)' }}>Uses:</span>{data ? uses : 'Loading…'}
        <button type="button" onClick={() => onGo?.('integrations')} className="underline" style={{ color: 'var(--ink-link)' }}>Change in Integrations</button>
      </div>

      <AutoInvestigationCard />
      <TriageQualityCard />
      <AiUsageCard />

      <section className="flex flex-col px-[18px] py-1.5 rounded-[14px] border" style={panel}>
        <h3 className="mt-2.5 mb-1 text-[15px] font-semibold">What leaves your network</h3>
        {toggles.map(([k, t, d]) => (
          <div key={k} className="flex flex-wrap items-center gap-3 py-3 border-b last:border-b-0" style={{ borderColor: 'var(--surf-line)' }}>
            <span className="flex-[1_1_280px] flex flex-col gap-[3px]"><span className="text-[13.5px] font-medium">{t}</span><span className="text-[12.5px] leading-snug" style={{ color: 'var(--ink-faint)' }}>{d}</span></span>
            <Switch on={!!data?.policy?.[k]} onChange={(v) => set(k, v)} label={t} disabled={!canEdit || !data || busy === k || (k === 'send_identifiers' && data && !data.policy.allow_external)} />
          </div>
        ))}
        <span className="text-xs py-3" style={{ color: 'var(--ink-faint)' }}>Applies to the assistant, triage, investigations and playbooks. Model order and keys live in Integrations.</span>
      </section>
    </div>
  );
}
